<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import SomenteLeituraOperadoMotor from '../components/SomenteLeituraOperadoMotor.vue';
import Chave from '../components/Chave.vue';
import { comportamentoInicial, podeEditar } from '../comportamento';

const { t } = useI18n();
const carregando = ref(true);
const salvando = ref(false);
const erro = ref('');
const plano = ref(null);
const personaId = ref(null);
const bolsa = ref(comportamentoInicial({}));

onMounted(async () => {
  try {
    const { data: status } = await AgenteIaAPI.status();
    plano.value = status.plano;
    personaId.value = status.persona?.id;
    const { data: persona } = await AgenteIaAPI.persona(personaId.value);
    bolsa.value = comportamentoInicial(persona.settings || {});
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.COMPORTAMENTO.ERRO');
  } finally {
    carregando.value = false;
  }
});

const trancado = chave =>
  podeEditar(chave, plano.value) ? '' : t('AGENTE_IA.DISPONIVEL_NO_AVANCADO');
const maxPassos = computed(() => plano.value?.limites?.passos_follow_up || 0);

const adicionarPasso = () => {
  bolsa.value.followUp.steps.push({
    delayValue: 1,
    delayUnit: 'hours',
    instructions: '',
  });
};
const removerPasso = indice => bolsa.value.followUp.steps.splice(indice, 1);

const salvar = async () => {
  salvando.value = true;
  const settings = Object.fromEntries(
    Object.entries(bolsa.value).filter(([chave]) =>
      podeEditar(chave, plano.value)
    )
  );
  try {
    await AgenteIaAPI.atualizarPersona(personaId.value, { settings });
    useAlert(t('AGENTE_IA.SALVO'));
  } catch (e) {
    useAlert(e?.response?.data?.error || t('AGENTE_IA.COMPORTAMENTO.ERRO'));
  } finally {
    salvando.value = false;
  }
};

const classeCampo =
  'px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12';
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.COMPORTAMENTO.TITULO')"
    :description="t('AGENTE_IA.COMPORTAMENTO.DESCRICAO')"
  >
    <SomenteLeituraOperadoMotor>
      <p v-if="carregando" class="text-sm text-n-slate-11">
        {{ t('AGENTE_IA.CARREGANDO') }}
      </p>
      <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

      <template v-else>
        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.COMPORTAMENTO.RITMO') }}
          </h2>
          <Chave
            v-model="bolsa.debounce.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.RAJADA.TITULO')"
            :ajuda="t('AGENTE_IA.COMPORTAMENTO.RAJADA.AJUDA')"
          />
          <label
            v-if="bolsa.debounce.enabled"
            class="flex items-center gap-2 pl-3 text-xs text-n-slate-11"
          >
            {{ t('AGENTE_IA.COMPORTAMENTO.RAJADA.SEGUNDOS') }}
            <input
              v-model.number="bolsa.debounce.windowSeconds"
              type="number"
              min="3"
              max="120"
              :class="classeCampo"
              class="w-24"
            />
          </label>
          <Chave
            v-model="bolsa.split.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.BALOES.TITULO')"
            :ajuda="t('AGENTE_IA.COMPORTAMENTO.BALOES.AJUDA')"
          />
          <label
            v-if="bolsa.split.enabled"
            class="flex items-center gap-2 pl-3 text-xs text-n-slate-11"
          >
            {{ t('AGENTE_IA.COMPORTAMENTO.BALOES.TAMANHO') }}
            <input
              v-model.number="bolsa.split.maxChars"
              type="number"
              min="80"
              max="4000"
              :class="classeCampo"
              class="w-24"
            />
          </label>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.COMPORTAMENTO.SENTIDOS') }}
          </h2>
          <Chave
            v-model="bolsa.stt.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.AUDIO')"
          />
          <Chave
            v-model="bolsa.vision.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.IMAGEM')"
          />
          <Chave
            v-model="bolsa.tts.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.VOZ')"
            :trancado="trancado('tts')"
          />
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.TITULO') }}
          </h2>
          <Chave
            v-model="bolsa.followUp.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.LIGAR')"
            :ajuda="
              t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.AJUDA', {
                maximo: maxPassos,
              })
            "
          />
          <template v-if="bolsa.followUp.enabled">
            <div
              v-for="(passo, indice) in bolsa.followUp.steps"
              :key="indice"
              class="flex flex-col gap-2 p-3 ml-3 rounded-lg border border-n-weak"
            >
              <div class="flex items-center gap-2 text-xs text-n-slate-11">
                {{ t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.DEPOIS_DE') }}
                <input
                  v-model.number="passo.delayValue"
                  type="number"
                  min="1"
                  :class="classeCampo"
                  class="w-20"
                />
                <select v-model="passo.delayUnit" :class="classeCampo">
                  <option
                    v-for="unidade in ['minutes', 'hours', 'days']"
                    :key="unidade"
                    :value="unidade"
                  >
                    {{
                      t(
                        `AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.UNIDADES.${unidade.toUpperCase()}`
                      )
                    }}
                  </option>
                </select>
                <button
                  class="ml-auto text-n-ruby-11"
                  @click="removerPasso(indice)"
                >
                  {{ t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.REMOVER') }}
                </button>
              </div>
              <textarea
                v-model="passo.instructions"
                rows="2"
                :class="classeCampo"
                :placeholder="
                  t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.O_QUE_DIZER')
                "
              />
            </div>
            <button
              v-if="bolsa.followUp.steps.length < maxPassos"
              class="self-start ml-3 text-sm text-n-brand"
              @click="adicionarPasso"
            >
              {{ t('AGENTE_IA.COMPORTAMENTO.FOLLOW_UP.ADICIONAR') }}
            </button>
          </template>
        </section>

        <section class="flex flex-col gap-2">
          <h2 class="text-sm font-medium text-n-slate-12">
            {{ t('AGENTE_IA.COMPORTAMENTO.MEMORIA.TITULO') }}
          </h2>
          <Chave
            v-model="bolsa.memory.compaction.enabled"
            :rotulo="t('AGENTE_IA.COMPORTAMENTO.MEMORIA.LIGAR')"
            :ajuda="t('AGENTE_IA.COMPORTAMENTO.MEMORIA.AJUDA')"
          />
          <p class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.COMPORTAMENTO.HORARIO') }}
          </p>
        </section>

        <div>
          <Button
            :label="t('AGENTE_IA.SALVAR')"
            :is-loading="salvando"
            @click="salvar"
          />
        </div>
      </template>
    </SomenteLeituraOperadoMotor>
  </AgenteIaLayout>
</template>
